// Mock email service for development
export const sendOTPEmail = async (to, otp, isActivation = true) => {
  const subject = isActivation ? 'Account Activation OTP' : 'Two-Factor Authentication (2FA) Code';
  const message = `
    To: ${to}
    Subject: ${subject}
    
    Your ${isActivation ? 'account activation' : 'two-factor authentication'} code is: ${otp}
    
    This code will expire in 5 minutes.
    If you didn't request this code, please ignore this email.
    
    Best regards,
    Arriva Admin Portal Team
  `;

  // In development, log the email to console
  console.log('\n📧 Email sent:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send cash ready for pickup notification
export const sendCashReadyNotification = async (to, transactionData) => {
  const subject = 'Cash Ready for Pickup';
  const message = `
    To: ${to}
    Subject: ${subject}
    
    Dear Customer,
    
    Your cash is now ready for pickup at our designated location.
    
    Transaction Details:
    - Reference: ${transactionData.reference}
    - Amount: ${transactionData.source_currency_code} ${transactionData.source_amount}
    - Pickup Location: [Location will be provided by admin]
    
    Please bring your identification documents and the disbursement code that will be provided to you.
    
    Best regards,
    Kadavra Team
  `;

  // In development, log the email to console
  console.log('\n📧 Cash Ready Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send cash collected notification
export const sendCashCollectedNotification = async (to, transactionData) => {
  const subject = 'Cash Collection Confirmed';
  const message = `
    To: ${to}
    Subject: ${subject}
    
    Dear Customer,
    
    Your cash collection has been confirmed successfully.
    
    Transaction Details:
    - Reference: ${transactionData.reference}
    - Amount: ${transactionData.source_currency_code} ${transactionData.source_amount}
    - Collection Date: ${new Date().toLocaleDateString()}
    
    Thank you for using our services!
    
    Best regards,
    Kadavra Team
  `;

  // In development, log the email to console
  console.log('\n📧 Cash Collected Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send admin notification for cash pickup events
export const sendAdminCashPickupNotification = async (adminEmail, transactionData, eventType) => {
  const subject = `Cash Pickup ${eventType === 'ready' ? 'Ready' : 'Collected'} - ${transactionData.reference}`;
  const message = `
    To: ${adminEmail}
    Subject: ${subject}
    
    Admin Notification,
    
    A cash transaction has been ${eventType === 'ready' ? 'marked as ready for pickup' : 'collected'}.
    
    Transaction Details:
    - Reference: ${transactionData.reference}
    - Customer: ${transactionData.user_email}
    - Amount: ${transactionData.source_currency_code} ${transactionData.source_amount}
    - Status: ${eventType === 'ready' ? 'Ready for Pickup' : 'Collected'}
    
    Best regards,
    Kadavra Admin System
  `;

  // In development, log the email to console
  console.log('\n📧 Admin Cash Pickup Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send user notification for upgrade request approval
export const sendUpgradeApprovedNotification = async (userEmail, requestData) => {
  const subject = 'Account Upgrade Approved';
  const message = `
    To: ${userEmail}
    Subject: ${subject}
    
    Dear User,
    
    Congratulations! Your account upgrade request has been approved.
    
    Request Details:
    - Request ID: ${requestData.id}
    - Type: Account Upgrade
    - Status: Approved
    - Approved Date: ${new Date().toLocaleDateString()}
    
    Your account has been upgraded to premium status. You can now transact at higher limits.
    
    Thank you for choosing Kadavra!
    
    Best regards,
    Kadavra Team
  `;

  // In development, log the email to console
  console.log('\n📧 Upgrade Approved Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send user notification for upgrade request decline
export const sendUpgradeDeclinedNotification = async (userEmail, requestData, adminComment) => {
  const subject = 'Account Upgrade Request Update';
  const message = `
    To: ${userEmail}
    Subject: ${subject}
    
    Dear User,
    
    Your account upgrade request has been reviewed.
    
    Request Details:
    - Request ID: ${requestData.id}
    - Type: Account Upgrade
    - Status: Declined
    - Review Date: ${new Date().toLocaleDateString()}
    
    Reason for Decline:
    ${adminComment}
    
    Please review the feedback and submit a new request with updated documentation if needed.
    
    Best regards,
    Kadavra Team
  `;

  // In development, log the email to console
  console.log('\n📧 Upgrade Declined Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};

// Send admin notification for new upgrade requests
export const sendAdminUpgradeRequestNotification = async (adminEmail, requestData) => {
  const subject = `New Upgrade Request - ${requestData.id}`;
  const message = `
    To: ${adminEmail}
    Subject: ${subject}
    
    Admin Notification,
    
    A new account upgrade request has been submitted and requires your review.
    
    Request Details:
    - Request ID: ${requestData.id}
    - Type: Account Upgrade
    - Status: Pending Review
    - Submitted Date: ${new Date(requestData.created).toLocaleDateString()}
    
    Please review the utility bill and approve or decline the request.
    
    Best regards,
    Kadavra Admin System
  `;

  // In development, log the email to console
  console.log('\n📧 Admin Upgrade Request Notification:');
  console.log('----------------------------------------');
  console.log(message);
  console.log('----------------------------------------\n');

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  return {
    success: true,
    messageId: Math.random().toString(36).substring(7)
  };
};
